import { platformClient } from './platform.client';
import { LoginResponse, RefreshTokenResponse, InviteVerifyResponse } from '../types';
import { StatusResponse } from '../../../types/api';

export interface LoginRequest {
  email: string;
  password: string;
  expected_role?: string;
  login_portal?: 'admin';
}

export interface LoginOptions {
  expectedRole?: string;
  loginPortal?: 'admin';
}

export interface RegisterRequest {
  email: string;
  password: string;
  role: 'PARENT' | 'STUDENT';
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface LogoutRequest {
  refresh_token: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface AcceptInviteRequest {
  token: string;
  password: string;
  name: string;
}

export interface UpdatePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface LmsLaunchRequest {
  assertion_jwt: string;
}

export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  const response = await platformClient.post<LoginResponse>('/auth/login', data, {
    headers: { 'x-skip-auth-redirect': '1' },
    // Avoid indefinite spinner when gateway/platform/Mongo is unreachable (axios default is no timeout).
    timeout: 45_000,
  });
  return response.data;
};

/** Exchange BanyanPro LMS assertion for TKM session tokens (POST /auth/lms/launch). */
export const lmsLaunch = async (data: LmsLaunchRequest): Promise<LoginResponse> => {
  const response = await platformClient.post<LoginResponse>('/auth/lms/launch', data, {
    headers: { 'x-skip-auth-redirect': '1' },
  });
  return response.data;
};

/** Production LMS authenticate — POST body only, no JWT in URL (POST /auth/authenticate). */
export const lmsAuthenticate = async (data: LmsLaunchRequest): Promise<LoginResponse> => {
  const response = await platformClient.post<LoginResponse>('/auth/authenticate', data, {
    headers: { 'x-skip-auth-redirect': '1' },
  });
  return response.data;
};

export const register = async (data: RegisterRequest): Promise<LoginResponse> => {
  const response = await platformClient.post<LoginResponse>('/auth/register', data);
  return response.data;
};

export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  const response = await platformClient.post<RefreshTokenResponse>('/auth/refresh', {
    refresh_token: refreshToken,
  });
  return response.data;
};

export const logout = async (refreshToken: string): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    '/auth/logout',
    { refresh_token: refreshToken },
    {
      headers: { 'x-skip-auth-redirect': '1' },
      timeout: 15_000,
    }
  );
  return response.data;
};

export const verifyInvite = async (token: string): Promise<InviteVerifyResponse> => {
  const response = await platformClient.get<InviteVerifyResponse>('/auth/invites/verify', {
    params: { token },
  });
  return response.data;
};

export const acceptInvite = async (data: AcceptInviteRequest): Promise<LoginResponse> => {
  const response = await platformClient.post<LoginResponse>('/auth/invites/accept', data);
  return response.data;
};

export const forgotPassword = async (data: ForgotPasswordRequest): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>('/auth/forgot-password', data);
  return response.data;
};

export const resetPassword = async (data: ResetPasswordRequest): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>('/auth/reset-password', data);
  return response.data;
};

export const updatePassword = async (data: UpdatePasswordRequest): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>('/auth/update-password', data, {
    headers: { 'x-skip-auth-redirect': '1' },
  });
  return response.data;
};

